import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Sparkles, ArrowRight, CheckCircle, Bookmark } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { logGenerationError } from '../lib/errorLogger';
import { FocusHeader } from '../components/FocusHeader';
import { StepIndicator } from '../components/StepIndicator';
import { getWizardSteps, LOCKED_AFTER_WRITING } from '../lib/wizardSteps';
import { WIZARD_EXIT_ROUTE, WIZARD_PATHS, sessionIdFromLocation, wizardUrl } from '../lib/wizardRoutes';
import { createIdea } from '../lib/capturedIdeas';

interface BlogIdea {
  id: string;
  session_id: string;
  user_id: string;
  source: string;
  idea_title: string;
  idea_content: string;
  idea_reason: string;
  status: string;
  presented_at: string;
}

const IDEA_GENERATION_TIMEOUT = 90000;
const MAX_RETRY_ATTEMPTS = 3;
const RETRY_DELAY = 2000;

interface FetchWithTimeoutOptions {
  timeout: number;
  signal?: AbortSignal;
}

async function fetchWithTimeout(
  url: string,
  options: RequestInit & FetchWithTimeoutOptions
): Promise<Response> {
  const { timeout, signal, ...fetchOptions } = options;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      ...fetchOptions,
      signal: controller.signal,
    });
    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    if (error instanceof Error && error.name === 'AbortError') {
      throw new Error('Timeout: Het duurt te lang, probeer opnieuw');
    }
    throw error;
  }
}

async function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

export function IdeaSelection() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const STORAGE_KEY = 'idea_selection_session_id';
  const locationSessionId = sessionIdFromLocation(location);
  const [sessionId] = useState<string | null>(() => {
    if (locationSessionId) {
      sessionStorage.setItem(STORAGE_KEY, locationSessionId);
      return locationSessionId;
    }
    return sessionStorage.getItem(STORAGE_KEY);
  });

  const [ideas, setIdeas] = useState<BlogIdea[]>([]);
  const [selectedIdea, setSelectedIdea] = useState<BlogIdea | null>(null);
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [retryAttempt, setRetryAttempt] = useState(0);
  const [savedIds, setSavedIds] = useState<Set<string>>(new Set());
  const hasInitialLoadedRef = useRef(false);

  useEffect(() => {
    if (sessionId && user?.id) {
      if (!hasInitialLoadedRef.current) {
        hasInitialLoadedRef.current = true;
        loadIdeas();
      }
    } else if (!sessionId) {
      navigate('/dashboard/new-blog', { replace: true });
    }
  }, [sessionId, user?.id]);

  const loadIdeas = async () => {
    if (!sessionId) return;

    try {
      setLoading(true);
      setError('');

      const { data: sessionData, error: sessionError } = await supabase
        .from('sessions')
        .select('*')
        .eq('id', sessionId)
        .maybeSingle();

      if (sessionError || !sessionData) {
        throw new Error('Sessie niet gevonden');
      }

      setSession(sessionData);

      const { data: ideasData, error: ideasError } = await supabase
        .from('blog_ideas')
        .select('*')
        .eq('session_id', sessionId)
        .in('status', ['presented', 'approved'])
        .order('presented_at', { ascending: false })
        .limit(sessionData.approved_idea_id ? 5 : 4);

      if (ideasError) {
        throw new Error('Kon ideeën niet laden');
      }

      if (!ideasData || ideasData.length === 0) {
        throw new Error('Geen ideeën gevonden');
      }

      setIdeas(ideasData);
    } catch (err: any) {
      console.error('Error loading ideas:', err);
      setError(err.message || 'Er ging iets mis bij het laden van ideeën');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateNew = async () => {
    if (!user || !sessionId) return;

    setGenerating(true);
    setError('');

    try {
      const oldIdeas = [...ideas];
      for (const idea of oldIdeas) {
        // Het gekozen idee blijft staan; alleen de aangeboden ideeën gaan weg.
        if (idea.id === session?.approved_idea_id) continue;
        await supabase
          .from('blog_ideas')
          .update({ status: 'rejected', rejection_reason: 'Gebruiker wilde andere ideeën zien' })
          .eq('id', idea.id);
      }

      console.log('[IdeaSelection] Generating 4 new ideas...');

      const { data: { session: authSession } } = await supabase.auth.getSession();
      const authToken = authSession?.access_token || (import.meta.env.VITE_SUPABASE_ANON_KEY);

      let lastError: Error | null = null;
      let result: any = null;

      for (let attempt = 1; attempt <= MAX_RETRY_ATTEMPTS; attempt++) {
        try {
          setRetryAttempt(attempt);
          console.log(`[IdeaSelection] Attempt ${attempt}/${MAX_RETRY_ATTEMPTS}`);

          const response = await fetchWithTimeout(
            `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/openai-generate`,
            {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${authToken}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                type: 'idea',
                session_id: sessionId,
                user_id: user.id,
                data: {},
              }),
              timeout: IDEA_GENERATION_TIMEOUT,
            }
          );

          result = await response.json();

          if (!response.ok) {
            console.error(`[IdeaSelection] OpenAI error on attempt ${attempt}:`, result);
            throw new Error(result.error || 'Fout bij het genereren van ideeën');
          }

          console.log(`[IdeaSelection] Success on attempt ${attempt}`);
          lastError = null;
          break;
        } catch (error) {
          lastError = error as Error;
          console.error(`[IdeaSelection] Error on attempt ${attempt}:`, error);

          const errorMessage = error instanceof Error ? error.message : String(error);
          const isTimeout = errorMessage.includes('Timeout');
          const isNetwork = errorMessage.includes('fetch') || errorMessage.includes('network');

          await logGenerationError({
            generationType: 'blog_idea',
            errorCategory: isTimeout ? 'TIMEOUT' : isNetwork ? 'NETWORK_ERROR' : 'API_ERROR',
            errorMessage: errorMessage,
            technicalError: error instanceof Error ? error.stack : undefined,
            retryAttempt: attempt - 1,
            sessionId: sessionId,
            additionalContext: {
              service: 'openai',
              apiType: 'idea_generation_multiple',
              timeout: IDEA_GENERATION_TIMEOUT,
              lastError: errorMessage,
              userId: user.id,
            },
          });

          if (attempt < MAX_RETRY_ATTEMPTS) {
            console.log(`[IdeaSelection] Waiting ${RETRY_DELAY}ms before retry...`);
            await sleep(RETRY_DELAY);
          }
        }
      }

      if (lastError) {
        throw new Error(
          `Na ${MAX_RETRY_ATTEMPTS} pogingen niet gelukt. Probeer het later opnieuw.`
        );
      }

      if (!result || !result.success) {
        throw new Error(result?.error || 'Geen resultaat ontvangen van AI');
      }

      setRetryAttempt(0);

      console.log('[IdeaSelection] Ideas generated and persisted server-side, reloading...');
      setSelectedIdea(null);
      await loadIdeas();
    } catch (err: any) {
      console.error('[IdeaSelection] Error generating new ideas:', err);
      setError(err.message || 'Er ging iets mis bij het genereren van nieuwe ideeën');
    } finally {
      setGenerating(false);
      setRetryAttempt(0);
    }
  };

  // Kies (of wissel) het idee van deze sessie en ga naar Richting.
  const approveIdea = async (idea: BlogIdea) => {
    if (!sessionId) return;

    try {
      const previousId: string | null = session?.approved_idea_id ?? null;

      if (previousId && previousId !== idea.id && session?.article_content) {
        // Idee wisselen kan niet meer zodra het artikel geschreven is.
        return;
      }

      setSelectedIdea(idea);

      if (previousId === idea.id) {
        navigate(wizardUrl(WIZARD_PATHS.direction, sessionId));
        return;
      }

      console.log('[IdeaSelection] Approving idea:', idea.id);

      const { error: approveError } = await supabase
        .from('blog_ideas')
        .update({
          status: 'approved',
          decided_at: new Date().toISOString(),
        })
        .eq('id', idea.id);
      if (approveError) throw approveError;

      const { error: sessionError } = await supabase
        .from('sessions')
        .update({
          state: 'idea_approved',
          approved_idea_id: idea.id,
          ...(previousId ? { article_direction: null, article_focus: null } : {}),
        })
        .eq('id', sessionId);
      if (sessionError) throw sessionError;

      if (previousId) {
        // Eerder gekozen idee terug naar 'aangeboden', zodat wisselen netjes blijft.
        const { error: revertError } = await supabase
          .from('blog_ideas')
          .update({ status: 'presented', decided_at: null })
          .eq('id', previousId);
        if (revertError) console.error('[IdeaSelection] Kon vorig idee niet terugzetten:', revertError);
      }

      sessionStorage.removeItem(STORAGE_KEY);
      console.log('[IdeaSelection] Navigating to article direction...');
      navigate(wizardUrl(WIZARD_PATHS.direction, sessionId));
    } catch (err) {
      console.error('[IdeaSelection] Error approving idea:', err);
      setError('Er ging iets mis bij het goedkeuren van het idee');
    }
  };

  const handleContinue = () => {
    if (selectedIdea) void approveIdea(selectedIdea);
  };

  const handleWorkOut = (idea: BlogIdea) => {
    void approveIdea(idea);
  };

  const handleSaveIdea = async (idea: BlogIdea) => {
    if (!user?.id) return;
    try {
      await createIdea(user.id, 'ai_suggestie', idea.idea_title, idea.idea_content);
      setSavedIds(prev => new Set(prev).add(idea.id));
    } catch (err) {
      console.error('[IdeaSelection] Error saving idea:', err);
      setError('Er ging iets mis bij het bewaren van het idee');
    }
  };

  // Vast zodra het artikel geschreven is: idee wisselen kan dan niet meer.
  const articleWritten = !!session?.article_content;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F2F2EF]">
        <FocusHeader
          title="Kies je blog idee"
          onBack={() => navigate('/dashboard/new-blog')}
          onClose={() => navigate(WIZARD_EXIT_ROUTE)}
          hideSaveStatus
        />
        <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-center py-12">
            <div className="w-12 h-12 border-4 border-[#FF6B35] border-t-transparent rounded-full animate-spin" />
          </div>
        </main>
      </div>
    );
  }

  if (error && ideas.length === 0) {
    return (
      <div className="min-h-screen bg-[#F2F2EF]">
        <FocusHeader
          title="Kies je blog idee"
          onBack={() => navigate('/dashboard/new-blog')}
          onClose={() => navigate(WIZARD_EXIT_ROUTE)}
          hideSaveStatus
        />
        <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <p className="text-red-800 mb-4">{error}</p>
            <button
              onClick={() => navigate(WIZARD_EXIT_ROUTE)}
              className="btn-secondary"
            >
              Naar Mijn Blogs
            </button>
          </div>
        </main>
      </div>
    );
  }

  const steps = session ? getWizardSteps(session, 1) : getWizardSteps(null, 1);

  return (
    <div className="min-h-screen bg-[#F2F2EF]">
      <FocusHeader
        title="Kies je blog idee"
        onBack={() => navigate('/dashboard/new-blog')}
        onClose={() => navigate(WIZARD_EXIT_ROUTE)}
        hideSaveStatus
      />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <StepIndicator steps={steps} />

        <div className="mt-6 space-y-6">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-[#231F20] mb-2">
              Selecteer een blog idee
            </h2>
            <p className="text-gray-600">
              Kies het idee waar je verder mee wilt, of genereer 4 nieuwe ideeën
            </p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {articleWritten && (
            <div className="card border border-green-200 bg-green-50 text-sm text-gray-700">
              Het artikel is al geschreven. Je kunt het gekozen idee bekijken, maar niet meer wisselen. Een ander idee betekent een nieuwe blog.
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {ideas.map((idea) => (
              <div
                key={idea.id}
                className={`
                  relative rounded-xl border-2 transition-all p-6 flex flex-col
                  ${selectedIdea?.id === idea.id || session?.approved_idea_id === idea.id
                    ? 'border-[#FF6B35] bg-[#FF6B35]/5 shadow-lg ring-2 ring-[#FF6B35]/20'
                    : 'border-gray-200 bg-white'
                  }
                  ${generating ? 'opacity-50 pointer-events-none' : ''}
                `}
              >
                {session?.approved_idea_id === idea.id && (
                  <span className="absolute top-3 right-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-xs font-medium">
                    <CheckCircle className="w-3 h-3" aria-hidden="true" />
                    Gekozen
                  </span>
                )}
                <div className="flex-1">
                  <h3 className="font-bold text-[#231F20] mb-3 text-lg leading-snug pr-8">
                    {idea.idea_title}
                  </h3>
                  <p className="text-gray-700 mb-3 leading-relaxed">
                    {idea.idea_content}
                  </p>
                  {idea.idea_reason && (
                    <p className="text-sm text-gray-500 italic border-t border-gray-100 pt-3 mb-4">
                      {idea.idea_reason}
                    </p>
                  )}
                </div>
                <div className="flex gap-2 mt-4 pt-4 border-t border-gray-100">
                  <button
                    onClick={() => handleWorkOut(idea)}
                    disabled={generating || (articleWritten && session?.approved_idea_id !== idea.id)}
                    title={articleWritten && session?.approved_idea_id !== idea.id ? LOCKED_AFTER_WRITING : undefined}
                    className="btn-primary flex-1 flex items-center justify-center gap-1.5 text-sm py-2"
                  >
                    <ArrowRight className="w-4 h-4" />
                    {session?.approved_idea_id === idea.id ? 'Verder met dit idee' : 'Werk uit'}
                  </button>
                  <button
                    onClick={() => handleSaveIdea(idea)}
                    disabled={generating || savedIds.has(idea.id)}
                    className="btn-secondary flex items-center justify-center gap-1.5 text-sm py-2 px-4 disabled:opacity-60"
                  >
                    {savedIds.has(idea.id) ? (
                      <>
                        <CheckCircle className="w-4 h-4" />
                        Bewaard
                      </>
                    ) : (
                      <>
                        <Bookmark className="w-4 h-4" />
                        Bewaar
                      </>
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="space-y-3 max-w-2xl mx-auto">
            {selectedIdea && (
              <button
                onClick={handleContinue}
                className="btn-primary w-full flex items-center justify-center gap-2"
              >
                <ArrowRight className="w-5 h-5" />
                Doorgaan met dit idee
              </button>
            )}

            <button
              onClick={handleGenerateNew}
              disabled={generating || articleWritten}
              className="btn-secondary w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {generating ? (
                <>
                  <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                  {retryAttempt > 0 ? `Poging ${retryAttempt}/${MAX_RETRY_ATTEMPTS}...` : 'Ideeën genereren...'}
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5" />
                  Genereer 4 nieuwe ideeën
                </>
              )}
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}
